import { loadSupersetCharts, rememberSchemes, withColorScheme } from '../charts/superset-bundle.js';
import { createSupersetChartLifecycle } from '../charts/superset-chart-lifecycle.js';
import { focusInView } from './focus-in-view.js';
import {
    ariaLabel,
    caption,
    describeDrawn,
    fill,
    isEmptyContract,
    secondaryDescriptor,
    tableColumns,
    tableRows,
    valueDescriptor,
} from '../charts/superset-chart-describe.js';

/**
 * The Alpine half of <x-charts.superset-chart>: one chart from the blink-superset bundle, with the
 * accessibility Superset's renderer does not provide.
 *
 * Contracts arrive two ways, and the component cannot tell them apart: the server-rendered first
 * paint, and `superset-chart-data` on window, keyed by this host's id — from Livewire
 * (`$this->dispatch('superset-chart-data', id: …, contract: …)`) or from a page's own script.
 * The detail may also carry `status` ('loading' | 'failed' | 'empty'), `emptyMessage`, `title`,
 * `seriesNames`: series label => the name the sr-only table and the accessible name call it by,
 * `periods`: what the accessible table calls each point, keyed by the instant it is drawn at, for
 * a surface whose points only it can name, `periodClicks`: true to be told of clicks on the chart
 * (below), and, from script only, `tooltip`: an ECharts tooltip option that replaces Superset's,
 * `formatShare`: how that tooltip prints a slice's share, so the table reads the same, and
 * `styleColors`: the page's colours for what a contract's series styles draw (see
 * blink-superset-bundle.md).
 *
 * After each draw the host reports what it drew, `superset-chart-drawn` {id, names, colors},
 * bubbling from its root: a surface whose legend is its own HTML can swatch it in the colours the
 * chart really used, which only the bundle knows when the chart draws in a palette. A draw that
 * fails bubbles `superset-chart-failed` {id}.
 *
 * With `periodClicks` on, a click on the chart bubbles `superset-chart-period-click` {id, instant,
 * pointerType}: `instant` is the x value of the period the tooltip showed when the plot was clicked
 * (the bundle's onPeriodClick), or null for a click off the plot, which on a Big Number is its
 * figure. `pointerType` is the pointer that pressed ('mouse', 'touch', 'pen', or '' when none was
 * seen), so the surface can leave a tap to the tooltip. Off, nothing is wired and nothing bubbles.
 */

const DATA_EVENT = 'superset-chart-data';

/**
 * `{ids}`: a surface is done with these hosts, so what is held for them is dropped and a host that
 * mounts under one of those ids later starts from its first paint. A report's tabs send it when the
 * page changes tab (report-page.js): card ids are unique within the report, so a card's host mounts
 * again under the same id when its tab is opened again, and would otherwise replay whatever the
 * previous visit was sent.
 */
const FORGET_EVENT = 'superset-chart-forget';
const RETRY_EVENT = 'superset-chart-retry';
const PERIOD_CLICK_EVENT = 'superset-chart-period-click';

/** ECharts marks the element it draws in; a click inside one is the bundle's to report. */
const ECHARTS_ELEMENT = '[_echarts_instance_]';

/**
 * Bubbled each time a contract is drawn, with what was drawn: the series' (or slices') names and
 * the colours they were inked in, so a surface printing its own key under the chart (Superset's
 * legend is mouse-only, so the app's charts draw none) shows the real colours, whatever palette
 * and series order Superset chose. Only the colours: a key's entries must be on the page before
 * the chart draws, or printing them would move what was drawn (report-page.js), so a surface
 * writes them from what it sent.
 */
const DRAWN_EVENT = 'superset-chart-drawn';

/**
 * Bubbled when a draw fails, the bundle's or ECharts', a re-draw of a chart already shown included.
 * The chart and its sr-only table are gone, and nothing the surface sent says so, so a surface that
 * hides a figure from assistive tech because the table reads it (a donut's key, report-page.js)
 * learns here that it must expose it again. A failure the surface sent as a status it knows already.
 */
const FAILED_EVENT = 'superset-chart-failed';

/**
 * The latest detail per id. A host mounted after its event fired — under x-if, in a tab rendered
 * late — still draws it, instead of waiting for a payload that has already come and gone.
 */
const held = new Map();

/** The live host per id. One listener routes by id instead of every host filtering every event. */
const hosts = new Map();

let routing = false;

/** A status replaces the contract it announces, and a contract replaces the status. */
function remember(detail) {
    const next = { ...(held.get(detail.id) ?? {}), ...detail };

    if ('status' in detail && ! ('contract' in detail)) delete next.contract;
    if ('contract' in detail && ! ('status' in detail)) delete next.status;

    held.set(detail.id, next);
}

/**
 * Installed by the first host rather than at import: app.js imports this module on every page,
 * and a page that draws no chart should not listen for one.
 */
function route() {
    if (routing) return;
    routing = true;

    window.addEventListener(DATA_EVENT, (event) => {
        const detail = event.detail;
        if (! detail || typeof detail.id !== 'string') return;

        remember(detail);
        hosts.get(detail.id)?.(detail);
    });

    // wire:navigate keeps this module alive from page to page, and a card id is only unique within
    // its page — a held contract replayed into the next page's host would draw someone else's data.
    document.addEventListener('livewire:navigating', () => held.clear());

    window.addEventListener(FORGET_EVENT, (event) => {
        const ids = event.detail?.ids;
        if (Array.isArray(ids)) ids.forEach((id) => held.delete(id));
    });
}

function readJson(node) {
    return node ? JSON.parse(node.textContent || 'null') : null;
}

export default function SupersetChart(config = {}) {
    const text = config.text ?? {};

    // Kept in the closure, not as reactive properties: Alpine deep-proxies what it holds, and a
    // contract is copied on every draw. The lifecycle owns a React root, which must not be proxied.
    let lifecycle = null;
    let contract = null;
    let tooltip = null;
    let styleColors = null;
    let seriesNames = null;
    let formatShare = null;
    let periods = null;
    let periodClicks = false;
    let clickListeners = null;
    // The pointer that last pressed on the chart: 'mouse', 'touch', 'pen', or '' before any.
    let lastPointer = '';
    let receive = null;
    let lastSettled = null;

    return {
        sc_id: String(config.id ?? ''),
        sc_title: String(config.title ?? ''),
        sc_colorScheme: config.colorScheme ?? null,
        sc_text: {
            loading: text.loading ?? 'Loading…',
            loadFailed: text.loadFailed ?? 'This chart could not be loaded.',
            retry: text.retry ?? 'Retry',
            reload: text.reload ?? 'Reload page',
            noData: text.noData ?? 'No data for the selected period.',
            noDataCell: text.noDataCell ?? 'No data',
            period: text.period ?? 'Period',
            category: text.category ?? 'Category',
            value: text.value ?? 'Value',
            share: text.share ?? 'Share',
            goal: text.goal ?? 'Goal',
            periodStatus: text.periodStatus ?? ':period (:status)',
            typeLine: text.typeLine ?? 'Line',
            typeBar: text.typeBar ?? 'Bar',
            typeArea: text.typeArea ?? 'Area',
            typePie: text.typePie ?? 'Pie',
            typeDonut: text.typeDonut ?? 'Donut',
            typeGauge: text.typeGauge ?? 'Gauge',
            ariaSeries: text.ariaSeries ?? ':title: :type chart of :series over :count periods. The same data follows as a table.',
            ariaCategories: text.ariaCategories ?? ':title: :type chart of :series across :count categories. The same data follows as a table.',
            ariaSlices: text.ariaSlices ?? ':title: :type chart of :count slices. The same data follows as a table.',
            ariaTrend: text.ariaTrend ?? ':title: :value. :status :subheader The trend over :count periods follows as a table.',
            ariaHeadline: text.ariaHeadline ?? ':title: :value. :status :subheader',
            ariaGauge: text.ariaGauge ?? ':title: :type chart reading :value. :subheader',
            changeUp: text.changeUp ?? 'Up :change.',
            changeDown: text.changeDown ?? 'Down :change.',
            changeFlat: text.changeFlat ?? 'No change, :change.',
            changeUpBetter: text.changeUpBetter ?? 'Up :change, which is better.',
            changeUpWorse: text.changeUpWorse ?? 'Up :change, which is worse.',
            changeDownBetter: text.changeDownBetter ?? 'Down :change, which is better.',
            changeDownWorse: text.changeDownWorse ?? 'Down :change, which is worse.',
            goalMet: text.goalMet ?? 'On track against a goal of :goal.',
            goalMissed: text.goalMissed ?? 'Off track against a goal of :goal.',
            caption: text.caption ?? 'Data for :title',
            announceFailed: text.announceFailed ?? ':title could not be loaded.',
            announceEmpty: text.announceEmpty ?? ':title has no data for the selected period.',
            announceEmptyBecause: text.announceEmptyBecause ?? ':title: :value',
            announceLoaded: text.announceLoaded ?? ':title loaded.',
        },
        sc_state: 'loading',
        sc_drawn: false,
        sc_model: null,
        sc_emptyMessage: '',
        sc_announcement: '',
        sc_needsReload: false,

        init() {
            rememberSchemes(readJson(document.querySelector('script[data-superset-chart-schemes]')));

            lifecycle = createSupersetChartLifecycle({
                label: `chart ${this.sc_id}`,
                getContainer: () => this.$refs.canvas,
                getRoot: () => this.$root,
                loadBundle: loadSupersetCharts,
                lazy: Boolean(config.lazy),
                darkChrome: config.darkChrome ?? null,
                inheritFont: Boolean(config.inheritFont),
                animate: config.animate !== false,
                onDrawn: (mount, bundle, drawnContract) => this.sc_describe(mount, bundle, drawnContract),
                onError: (error, stage) => this.sc_fail(error, stage),
            });

            route();
            receive = (detail) => this.sc_ingest(detail);
            hosts.set(this.sc_id, receive);

            // An event that fired before this host existed is newer than the first paint — unless it
            // only renamed the chart or swapped its tooltip or colours, which still apply on top of it.
            const replay = held.get(this.sc_id) ?? {};
            this.sc_ingest(replay.contract || replay.status
                ? replay
                : { ...replay, id: this.sc_id, contract: readJson(this.$root.querySelector('script[data-superset-chart-initial]')) });
        },

        destroy() {
            if (hosts.get(this.sc_id) === receive) hosts.delete(this.sc_id);
            lifecycle?.cleanup();
            lifecycle = null;

            if (clickListeners) {
                this.$refs.canvas?.removeEventListener('pointerdown', clickListeners.pressed);
                this.$refs.canvas?.removeEventListener('click', clickListeners.clicked);
                clickListeners = null;
            }
        },

        sc_ingest(detail) {
            if (detail.title) this.sc_title = String(detail.title);
            if ('tooltip' in detail) tooltip = detail.tooltip ?? null;
            if ('styleColors' in detail) styleColors = detail.styleColors ?? null;
            if ('seriesNames' in detail) seriesNames = detail.seriesNames ?? null;
            if ('formatShare' in detail) formatShare = typeof detail.formatShare === 'function' ? detail.formatShare : null;
            if ('periods' in detail) periods = detail.periods ?? null;
            if ('periodClicks' in detail) {
                periodClicks = detail.periodClicks === true;
                if (periodClicks) this.sc_listenForClicks();
            }

            if (detail.status === 'failed') {
                contract = null;
                lifecycle.clear();
                this.sc_needsReload = false;
                this.sc_settle('failed');
                return;
            }

            if (detail.status === 'loading') {
                contract = null;
                lifecycle.clear();
                this.sc_state = 'loading';
                return;
            }

            // Nothing to draw, for a reason only the surface knows — every series switched off, a
            // source with no data yet. Said in the surface's own words, on screen and aloud.
            if (detail.status === 'empty') {
                contract = null;
                lifecycle.clear();
                this.sc_emptyMessage = detail.emptyMessage || this.sc_text.noData;
                this.sc_settle('empty', detail.emptyMessage);
                return;
            }

            if (! detail.contract) {
                // A tooltip, colours or series names on their own re-draw what is already there, so
                // a surface can swap them without re-sending the data. So do clicks switched on, which
                // the bundle wires as it mounts; switched off, the hook already reports nothing.
                const redraws = 'tooltip' in detail || 'styleColors' in detail || 'seriesNames' in detail || ('periodClicks' in detail && periodClicks);
                if (redraws && contract && this.sc_state === 'chart') this.sc_draw();
                return;
            }

            contract = withColorScheme(detail.contract, this.sc_colorScheme);

            if (isEmptyContract(contract)) {
                this.sc_emptyMessage = detail.emptyMessage || this.sc_text.noData;
                lifecycle.clear();
                this.sc_settle('empty', detail.emptyMessage);
                return;
            }

            this.sc_draw();
        },

        sc_draw() {
            // 'chart' before the draw, not after: the canvas box is display:none in every other
            // state, and a box with no width cannot be measured, so nothing could ever mount into it.
            this.sc_state = 'chart';
            this.sc_drawn = false;
            lifecycle.draw(contract, {
                tooltip,
                styleColors,
                onPeriodClick: periodClicks ? (instant) => this.sc_periodClick(instant) : null,
            });
        },

        /**
         * Listens on the chart's box once a surface first asks for clicks: which pointer pressed,
         * and a click off the plot (a Big Number's figure). A click on the plot is the bundle's to
         * report, through onPeriodClick, since only it knows the period the tooltip shows; the box
         * hears that click after the bundle has, and leaves it alone.
         */
        sc_listenForClicks() {
            const box = this.$refs.canvas;
            if (clickListeners || ! box) return;

            clickListeners = {
                pressed: (event) => {
                    lastPointer = typeof event.pointerType === 'string' ? event.pointerType : '';
                },
                clicked: (event) => {
                    if (this.sc_state !== 'chart' || event.target?.closest?.(ECHARTS_ELEMENT)) return;

                    this.sc_periodClick(null);
                },
            };
            box.addEventListener('pointerdown', clickListeners.pressed);
            box.addEventListener('click', clickListeners.clicked);
        },

        /** A click on the chart, reported to the surface only while it still asks for them. */
        sc_periodClick(instant) {
            if (! periodClicks) return;

            this.$dispatch(PERIOD_CLICK_EVENT, {
                id: this.sc_id,
                instant: typeof instant === 'number' && Number.isFinite(instant) ? instant : null,
                pointerType: lastPointer,
            });
        },

        sc_describe(mount, bundle, drawnContract) {
            const descriptor = valueDescriptor(drawnContract);
            const formatValue = descriptor ? bundle.formatterFor(descriptor) : String;
            const secondary = secondaryDescriptor(drawnContract);

            this.sc_model = describeDrawn(drawnContract, mount.props, {
                formatValue,
                formatSecondary: secondary ? bundle.formatterFor(secondary) : formatValue,
                formatShare,
                locale: document.documentElement.lang || undefined,
                seriesNames,
                periods,
            });
            this.sc_drawn = true;
            this.sc_settle('chart');
            this.$dispatch(DRAWN_EVENT, { id: this.sc_id, names: [...this.sc_model.names], colors: [...this.sc_model.colors] });
        },

        sc_fail(error, stage) {
            console.error('Superset chart failed to draw', { id: this.sc_id, error });
            this.sc_needsReload = stage === 'load';
            this.sc_settle('failed');
            this.$dispatch(FAILED_EVENT, { id: this.sc_id });
        },

        /**
         * Settle into a state, announcing the change — but never the FIRST settle. Every card on a
         * report settles on load, and the page's own status says it has loaded; a region per card
         * speaking at once would bury it. Chart-to-chart (new data for a chart already drawn) is
         * silent too, for the same reason: the table beside it has already changed.
         *
         * An empty chart whose surface said why is announced with that reason, the sentence its box
         * shows: "no data for the selected period" would be false of a chart whose reader has just
         * switched its last series off.
         */
        sc_settle(next, reason = null) {
            this.sc_state = next;

            const previous = lastSettled;
            lastSettled = next;
            if (previous === null || previous === next) return;

            if (next === 'empty' && reason) {
                this.sc_announce(fill(this.sc_text.announceEmptyBecause, { ':title': this.sc_title, ':value': reason }));
                return;
            }

            const key = { failed: 'announceFailed', empty: 'announceEmpty', chart: 'announceLoaded' }[next];
            this.sc_announce(fill(this.sc_text[key], { ':title': this.sc_title }));
        },

        /**
         * Cleared first and set on the next frame, so the same sentence twice in a row — a card that
         * fails, is retried and fails again — changes the region's text and is spoken again.
         */
        sc_announce(message) {
            this.sc_announcement = '';
            requestAnimationFrame(() => {
                this.sc_announcement = message;
            });
        },

        /**
         * Pressing Retry hides the button being pressed, which would drop focus to <body>. It is
         * parked first on the card's heading — the element marked data-card-heading inside the
         * nearest data-card ancestor, as the Site Analytics cards mark theirs — or on the figure,
         * and the page scrolls it clear of a pinned filter header or edit bar (focusInView()).
         *
         * A draw that failed here is re-drawn from the contract still held. A failure the server
         * reported has no contract to re-draw, so the server is asked for another: the event
         * reaches any Livewire listener declared with #[On('superset-chart-retry')].
         *
         * A bundle that did not download cannot be fetched again in this page: the browser keeps
         * the failed import, and a fresh URL cannot help (superset-bundle.js). So that failure's
         * button says "Reload page" and reloads the page, rather than offering a Retry that fails
         * again with nothing to show for it.
         */
        sc_retry() {
            if (this.sc_needsReload) {
                window.location.reload();
                return;
            }

            const heading = this.$root.closest('[data-card]')?.querySelector('[data-card-heading]');
            focusInView(heading ?? this.$refs.figure);

            if (contract) {
                this.sc_draw();
                return;
            }

            this.sc_state = 'loading';
            this.$dispatch(RETRY_EVENT, { id: this.sc_id });
        },

        get sc_ariaLabel() {
            return this.sc_drawn && this.sc_model
                ? ariaLabel(this.sc_model, this.sc_text, this.sc_title)
                : this.sc_text.loading;
        },

        get sc_retryLabel() {
            return this.sc_needsReload ? this.sc_text.reload : this.sc_text.retry;
        },

        get sc_caption() {
            return caption(this.sc_text, this.sc_title);
        },

        get sc_hasTable() {
            return this.sc_drawn && this.sc_model !== null && this.sc_model.kind !== 'headline' && this.sc_model.rows.length > 0;
        },

        get sc_tableColumns() {
            return this.sc_model ? tableColumns(this.sc_model, this.sc_text) : [];
        },

        get sc_tableRows() {
            return this.sc_model ? tableRows(this.sc_model, this.sc_text) : [];
        },
    };
}
